/**
 * Suggested nicknames for participants who never typed a name.
 *
 * The suggestion mirrors the avatar — the animal emoji and its color — so the
 * name a fresh participant sees in the input matches what everyone else will
 * see on their avatar. Same inputs, same nickname: it is deterministic, just
 * like the avatar itself.
 */
import { AVATAR_COLORS, AVATAR_EMOJIS } from "@/engine/state/avatarEmoji";
import {
  AvatarChoice,
  resolveAvatarColor,
  resolveAvatarEmoji,
} from "@/engine/state/avatarChoice";

/** Animal names in the same order as AVATAR_EMOJIS. */
const ANIMAL_NAMES = [
  "Fox",
  "Panda",
  "Lion",
  "Tiger",
  "Frog",
  "Monkey",
  "Unicorn",
  "Dragon",
  "Butterfly",
  "Octopus",
  "Crab",
  "Whale",
  "Flamingo",
  "Peacock",
  "Parrot",
  "Penguin",
  "Wolf",
  "Bear",
  "Koala",
  "Bunny",
  "Hamster",
  "Mouse",
  "Cat",
  "Dog",
  "Raccoon",
  "Cow",
  "Pig",
  "Chicken",
  "Chick",
  "Duck",
  "Eagle",
  "Owl",
  "Bee",
  "Bug",
  "Snail",
  "Lizard",
  "Turtle",
  "Snake",
  "Dino",
  "Crocodile",
] as const;

/** Color names in the same order as AVATAR_COLORS. Single words keep the combined nickname short. */
const COLOR_NAMES = [
  "Emerald",
  "Coral",
  "Teal",
  "Sunny",
  "Purple",
  "Orange",
  "Sky",
  "Pink",
  "Green",
  "Rose",
  "Indigo",
  "Amber",
  "Magenta",
  "Cyan",
  "Violet",
  "Red",
  "Azure",
  "Lime",
  "Tangerine",
  "Blue",
  "Aqua",
  "Lavender",
  "Scarlet",
  "Mint",
] as const;

function indexOf<T>(list: readonly T[], value: T): number {
  const index = list.indexOf(value);
  return index === -1 ? 0 : index;
}

/**
 * "Coral Fox"-style nickname matching the participant's avatar. Falls back to
 * the first entries if the emoji/color somehow resolve outside the palettes.
 */
export function suggestNickname(
  peerId: string,
  choice: AvatarChoice = { emoji: null, color: null },
): string {
  const emoji = resolveAvatarEmoji(peerId, choice.emoji ?? undefined);
  const color = resolveAvatarColor(peerId, choice.color ?? undefined);
  const animal = ANIMAL_NAMES[indexOf(AVATAR_EMOJIS, emoji)];
  const colorName = COLOR_NAMES[indexOf(AVATAR_COLORS, color)];
  return `${colorName} ${animal}`;
}
